/* Shreya — collection & search browsing.
   Live filtering and sorting through the Section Rendering API, history support,
   load more / infinite paging, grid column switcher and the horizontal filter bar
   (disclosure buttons: dropdown pills on desktop, accordions in the drawer / sidebar).
   Without JavaScript every control still works: the filter form submits as a normal GET. */
(() => {
  if (customElements.get('collection-browser')) return;

  const desktop = window.matchMedia('(min-width: 990px)');
  const VIEW_KEY = 'shreya:grid-view';
  const pageCache = new Map();
  const GROUP = '[data-filter-group]';

  const readView = () => {
    try { return JSON.parse(window.localStorage.getItem(VIEW_KEY)) || {}; } catch (_) { return {}; }
  };
  const storeView = (view) => {
    try { window.localStorage.setItem(VIEW_KEY, JSON.stringify(view)); } catch (_) { /* storage blocked */ }
  };

  /* Remove the section_id parameter Shopify may echo into links. */
  const cleanUrl = (href) => {
    const url = new URL(href, window.location.href);
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}`;
  };
  const withSection = (href, id) => {
    const url = new URL(href, window.location.href);
    url.searchParams.set('section_id', id);
    return `${url.pathname}${url.search}`;
  };

  class CollectionBrowser extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facets-form]');
      this.main = this.querySelector('[data-collection-main]');
      this.panel = this.querySelector('[data-facets-panel]');
      this.drawer = this.panel ? this.panel.closest('drawer-panel') : null;
      this.timer = null;

      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleKey = this.handleKey.bind(this);
      this.handleOutside = this.handleOutside.bind(this);
      this.handlePop = this.handlePop.bind(this);
      this.handleBreakpoint = () => { this.closeDropdowns(); this.refreshArrows(); };

      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('submit', this.handleSubmit);
      this.addEventListener('click', this.handleClick);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('pointerdown', () => { this.tabbing = false; });
      this.addEventListener('keydown', this.handleKey);
      this.addEventListener('drawer:close', () => {
        if (this.dataset.layout !== 'drawer' && this.panel) this.panel.removeAttribute('aria-hidden');
      });
      document.addEventListener('click', this.handleOutside);
      window.addEventListener('popstate', this.handlePop);
      desktop.addEventListener('change', this.handleBreakpoint);

      try {
        window.history.replaceState({ ...(window.history.state || {}), facetsSection: this.sectionId }, '');
      } catch (_) { /* sandboxed */ }

      if (this.querySelector('[data-view-switcher]')) this.applyView(readView());
      this.initBar();
      this.watchInfinite();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutside);
      window.removeEventListener('popstate', this.handlePop);
      desktop.removeEventListener('change', this.handleBreakpoint);
      if (this.infinite) this.infinite.disconnect();
      if (this.barObserver) this.barObserver.disconnect();
      if (this.request) this.request.abort();
    }

    /* ---------- building the url ---------- */
    get isDropdownMode() {
      return this.dataset.layout === 'horizontal' && desktop.matches;
    }

    buildUrl() {
      const params = new URLSearchParams();
      new FormData(this.form).forEach((value, key) => {
        if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
      });
      const path = new URL(this.form.getAttribute('action') || window.location.pathname, window.location.href).pathname;
      const query = params.toString();
      return query ? `${path}?${query}` : path;
    }

    queue(delay = 0) {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.load(this.buildUrl()), delay);
    }

    /* ---------- events ---------- */
    handleChange(event) {
      const el = event.target;
      if (!this.form || el.form !== this.form) return;
      if (el.matches('[data-range]')) { this.syncFromSlider(el); this.queue(0); return; }
      if (el.matches('[data-price-input]')) { this.syncFromField(el); this.queue(250); return; }
      this.queue(el.type === 'checkbox' ? 120 : 0);
    }

    handleInput(event) {
      const el = event.target;
      if (el.matches('[data-range]')) this.syncFromSlider(el);
      else if (el.matches('[data-price-input]')) { this.syncFromField(el); this.queue(900); }
    }

    handleSubmit(event) {
      if (event.target !== this.form) return;
      event.preventDefault();
      this.queue(0);
      if (this.drawer && this.drawer.isOpen) this.drawer.close();
    }

    handleClick(event) {
      const t = event.target;

      const facetLink = t.closest('[data-facet-link], .pagination a');
      if (facetLink && facetLink.href) {
        event.preventDefault();
        this.load(cleanUrl(facetLink.href));
        return;
      }

      const more = t.closest('[data-load-more]');
      if (more) { event.preventDefault(); this.loadMore(); return; }

      const showMore = t.closest('[data-show-more]');
      if (showMore) { this.toggleValues(showMore); return; }

      const toggle = t.closest('[data-filter-toggle]');
      if (toggle) {
        const group = toggle.closest(GROUP);
        if (group) this.setOpen(group, !group.classList.contains('is-open'));
        return;
      }

      const scroller = t.closest('[data-facets-scroll]');
      if (scroller) {
        const list = this.querySelector('[data-facets-list]');
        if (list) list.scrollBy({ left: Number(scroller.dataset.facetsScroll) * list.clientWidth * 0.6, behavior: 'smooth' });
        return;
      }

      const colBtn = t.closest('[data-view-cols], [data-view-cols-mobile]');
      if (colBtn) {
        const view = readView();
        if (colBtn.dataset.viewCols) view.cols = colBtn.dataset.viewCols;
        if (colBtn.dataset.viewColsMobile) view.mobile = colBtn.dataset.viewColsMobile;
        storeView(view);
        this.applyView(view);
      }
    }

    /* Open or close one filter group. In the desktop bar only one dropdown is open at a time. */
    setOpen(group, open) {
      const toggle = group.querySelector('[data-filter-toggle]');
      if (open && this.isDropdownMode) {
        this.querySelectorAll(`${GROUP}.is-open`).forEach((g) => { if (g !== group) this.setOpen(g, false); });
      }
      group.classList.toggle('is-open', open);
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
      if (open && this.isDropdownMode) this.placeDropdown(group);
    }

    get openDropdown() {
      return this.isDropdownMode ? this.querySelector(`${GROUP}.is-open`) : null;
    }

    handleKey(event) {
      this.tabbing = event.key === 'Tab';
      if (event.key !== 'Escape') return;
      const open = this.openDropdown;
      if (!open) return;
      event.stopPropagation();
      this.setOpen(open, false);
      const toggle = open.querySelector('[data-filter-toggle]');
      if (toggle) toggle.focus();
    }

    handleOutside(event) {
      if (!this.openDropdown) return;
      if (event.target.closest && event.target.closest(GROUP)) return;
      this.closeDropdowns();
    }

    /* Tabbing out of an open dropdown closes it. Only keyboard moves count: a mouse press on a
       label inside the panel also shifts focus, and focus lost to a re-render has no target. */
    handleFocusOut(event) {
      const open = this.openDropdown;
      const next = event.relatedTarget;
      if (!this.tabbing || !open || !next || open.contains(next)) return;
      if (open.contains(event.target)) this.setOpen(open, false);
    }

    handlePop(event) {
      if (event.state && event.state.facetsSection === this.sectionId) {
        this.load(window.location.pathname + window.location.search, { push: false });
      }
    }

    /* ---------- price range ---------- */
    priceParts(el) {
      const box = el.closest('[data-price-range]');
      return {
        box,
        max: Number(box.dataset.max) || 0,
        track: box.querySelector('.collection-price__track'),
        sliderMin: box.querySelector('[data-range="min"]'),
        sliderMax: box.querySelector('[data-range="max"]'),
        fieldMin: box.querySelector('[data-price-input="min"]'),
        fieldMax: box.querySelector('[data-price-input="max"]'),
      };
    }

    paintTrack(p, low, high) {
      if (!p.track || !p.max) return;
      p.track.style.setProperty('--low', `${(low / p.max) * 100}%`);
      p.track.style.setProperty('--high', `${(high / p.max) * 100}%`);
    }

    syncFromSlider(el) {
      const p = this.priceParts(el);
      let low = Number(p.sliderMin.value);
      let high = Number(p.sliderMax.value);
      if (low > high) {
        if (el === p.sliderMin) { low = high; p.sliderMin.value = low; } else { high = low; p.sliderMax.value = high; }
      }
      p.fieldMin.value = low > 0 ? low : '';
      p.fieldMax.value = high < p.max ? high : '';
      this.paintTrack(p, low, high);
    }

    syncFromField(el) {
      const p = this.priceParts(el);
      const clamp = (n) => Math.min(Math.max(n, 0), p.max);
      const low = p.fieldMin.value === '' ? 0 : clamp(Number(p.fieldMin.value));
      const high = p.fieldMax.value === '' ? p.max : clamp(Number(p.fieldMax.value));
      if (p.sliderMin) p.sliderMin.value = Math.min(low, high);
      if (p.sliderMax) p.sliderMax.value = Math.max(low, high);
      this.paintTrack(p, Math.min(low, high), Math.max(low, high));
    }

    toggleValues(button) {
      const list = button.parentElement.querySelector('[data-filter-values]');
      if (!list) return;
      const open = list.classList.toggle('is-expanded');
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? button.dataset.less : button.dataset.more;
    }

    /* ---------- horizontal bar ---------- */
    initBar() {
      const list = this.querySelector('[data-facets-list]');
      if (!list) return;
      list.addEventListener('scroll', window.Theme.throttle(() => {
        this.refreshArrows();
        const open = this.openDropdown;
        if (open) this.placeDropdown(open);
      }), { passive: true });
      this.barObserver = new ResizeObserver(() => this.refreshArrows());
      this.barObserver.observe(list);
      if (this.isDropdownMode) this.closeDropdowns();
      this.refreshArrows();
    }

    refreshArrows() {
      const list = this.querySelector('[data-facets-list]');
      if (!list) return;
      const max = list.scrollWidth - list.clientWidth;
      const show = this.isDropdownMode && max > 4;
      const prev = this.querySelector('[data-facets-scroll="-1"]');
      const next = this.querySelector('[data-facets-scroll="1"]');
      if (prev) prev.hidden = !show || list.scrollLeft <= 4;
      if (next) next.hidden = !show || list.scrollLeft >= max - 4;
    }

    placeDropdown(group) {
      const bar = this.querySelector('[data-facets-bar]');
      const panel = group.querySelector('[data-filter-panel]');
      const toggle = group.querySelector('[data-filter-toggle]');
      if (!bar || !panel || !toggle) return;
      const barBox = bar.getBoundingClientRect();
      const tabBox = toggle.getBoundingClientRect();
      const width = panel.offsetWidth || 300;
      const left = Math.max(0, Math.min(tabBox.left - barBox.left, barBox.width - width));
      panel.style.setProperty('--panel-left', `${left}px`);
    }

    closeDropdowns() {
      if (!this.isDropdownMode) return;
      this.querySelectorAll(`${GROUP}.is-open`).forEach((g) => this.setOpen(g, false));
    }

    /* ---------- grid view ---------- */
    applyView(view) {
      if (!this.main) return;
      if (view.cols && this.querySelector(`[data-view-cols="${view.cols}"]`)) this.main.dataset.cols = view.cols;
      if (view.mobile && this.querySelector(`[data-view-cols-mobile="${view.mobile}"]`)) this.main.dataset.colsMobile = view.mobile;
      this.querySelectorAll('[data-view-cols]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewCols === this.main.dataset.cols)));
      this.querySelectorAll('[data-view-cols-mobile]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewColsMobile === this.main.dataset.colsMobile)));
    }

    /* ---------- loading ---------- */
    async fetchPage(url, signal) {
      if (pageCache.has(url)) return pageCache.get(url);
      const res = await fetch(withSection(url, this.sectionId), { signal });
      if (!res.ok) throw new Error(res.statusText);
      const html = await res.text();
      pageCache.set(url, html);
      return html;
    }

    async load(url, { push = true } = {}) {
      if (this.request) this.request.abort();
      const request = new AbortController();
      this.request = request;
      const results = this.querySelector('[data-results]');
      if (results) results.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        const html = await this.fetchPage(url, request.signal);
        if (this.request !== request) return;
        this.swap(html);
        if (push) window.history.pushState({ facetsSection: this.sectionId }, '', url);
      } catch (error) {
        if (error.name !== 'AbortError') {
          if (window.Theme.toast) window.Theme.toast(window.Theme.strings.error || error.message);
        }
      } finally {
        if (this.request === request) {
          this.request = null;
          if (results) results.classList.remove('is-loading');
          this.removeAttribute('aria-busy');
        }
      }
    }

    swap(html) {
      const doc = window.Theme.parseHTML(html);
      const fresh = doc.querySelector(`collection-browser[data-section-id="${this.sectionId}"]`) || doc;

      this.swapFilters(fresh);
      fresh.querySelectorAll('[data-region]').forEach((node) => {
        const current = this.querySelector(`[data-region="${node.dataset.region}"]`);
        if (current) current.innerHTML = node.innerHTML;
      });

      const results = this.querySelector('[data-results]');
      if (results) window.Theme.initReveal(results);
      this.refreshArrows();
      this.watchInfinite();

      const drawerOpen = this.drawer && this.drawer.isOpen;
      if (!drawerOpen && this.main) {
        const top = this.main.getBoundingClientRect().top;
        if (top < 0) {
          const offset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
          window.scrollTo({ top: window.scrollY + top - offset - 16, behavior: 'smooth' });
        }
      }
    }

    swapFilters(fresh) {
      const list = this.querySelector('[data-facets-list]');
      const next = fresh.querySelector('[data-facets-list]');
      if (!list || !next) return;
      const openIds = new Set(Array.from(list.querySelectorAll(`${GROUP}.is-open`), (g) => g.dataset.filterId));
      const expandedIds = new Set(Array.from(list.querySelectorAll('[data-filter-values].is-expanded'), (ul) => ul.closest(GROUP).dataset.filterId));
      const focused = document.activeElement && list.contains(document.activeElement) ? document.activeElement.id : null;
      const scrollLeft = list.scrollLeft;

      list.innerHTML = next.innerHTML;

      list.querySelectorAll(GROUP).forEach((g) => {
        const isOpen = openIds.has(g.dataset.filterId);
        g.classList.toggle('is-open', isOpen);
        const toggle = g.querySelector('[data-filter-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
        if (expandedIds.has(g.dataset.filterId)) {
          const btn = g.querySelector('[data-show-more]');
          if (btn) this.toggleValues(btn);
        }
      });
      list.scrollLeft = scrollLeft;
      const open = this.openDropdown;
      if (open) this.placeDropdown(open);
      if (focused) {
        const el = document.getElementById(focused);
        if (el) el.focus({ preventScroll: true });
      }
    }

    async loadMore() {
      const button = this.querySelector('[data-load-more]');
      if (!button || this.loadingMore) return;
      this.loadingMore = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');
      const hadFocus = document.activeElement === button;
      try {
        const html = await this.fetchPage(cleanUrl(button.href));
        const doc = window.Theme.parseHTML(html);
        const grid = this.querySelector('[data-product-grid]');
        const items = Array.from(doc.querySelectorAll('[data-product-grid] > li'));
        if (grid && items.length) {
          items.forEach((li) => grid.appendChild(document.adoptNode(li)));
          window.Theme.initReveal(grid);
          if (hadFocus) {
            const link = items[0].querySelector('a[href]:not([tabindex="-1"])');
            if (link) link.focus({ preventScroll: true });
          }
        }
        const paging = this.querySelector('[data-paging]');
        const nextPaging = doc.querySelector('[data-paging]');
        if (paging && nextPaging) paging.innerHTML = nextPaging.innerHTML;
        const count = this.querySelector('[data-region="count"]');
        const nextCount = doc.querySelector('[data-region="count"]');
        if (count && nextCount) count.innerHTML = nextCount.innerHTML;
      } catch (error) {
        if (window.Theme.toast) window.Theme.toast(window.Theme.strings.error || error.message);
      } finally {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        this.loadingMore = false;
        this.watchInfinite();
      }
    }

    watchInfinite() {
      if (this.infinite) this.infinite.disconnect();
      if (this.dataset.pagination !== 'infinite' || !('IntersectionObserver' in window)) return;
      const button = this.querySelector('[data-load-more]');
      if (!button) return;
      this.infinite = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.infinite.disconnect();
          this.loadMore();
        }
      }, { rootMargin: '0px 0px 500px 0px' });
      this.infinite.observe(button);
    }
  }

  customElements.define('collection-browser', CollectionBrowser);
})();
